"use client";

import React from "react";
import { MapPin, Phone, Clock, CircleCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const SERVICES = ["Lawn Maintenance", "Mulching", "Landscape Design", "Tree and Shrub Care", "Spring and Fall Cleanups", "Patios", "Retaining Walls", "Snow Removal"];
function EstimateForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Request Received!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription className="sr-only">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(412) 555-1234" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Best number to reach you</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormDescription className="sr-only">We'll send the estimate to this address</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="address" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Property Address</FormLabel>
              <FormControl>
                <Input placeholder="1234 Oak St, Pittsburgh, PA" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Where the work will be performed</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Service Needed</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SERVICES.map((service, i) => <SelectItem key={service} value={service} data-index={i}>
                      {service}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="details" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Project Details</FormLabel>
              <FormControl>
                <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive resize-y" placeholder="Brief description of the work you need (e.g., size of lawn, specific concerns)" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                The more information you provide, the more accurate your estimate will be
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : "Get My Free Estimate"}
      </Button>
    </Form>;
}
export default function RequestAFreeEstimate() {
  return <section id="request-a-free-estimate" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-muted/30 -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif tracking-tight mb-4">
            Request a Free Estimate
          </h2>
          <p className="text-lg text-muted-foreground">
            Ready to transform your outdoor space? Fill out the form below or give us a call, and our team of landscaping experts will provide you with a comprehensive, no-obligation estimate.
          </p>
        </div>

        {}
        <div className="border-4 border-primary rounded-2xl bg-card shadow-xl overflow-hidden flex flex-col lg:flex-row">
          
          {}
          <div className="lg:w-5/12 bg-primary text-primary-foreground p-8 md:p-12 flex flex-col justify-between relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none" />
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-6 font-serif">Contact Information</h3>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-foreground/10 p-3 rounded-full shrink-0">
                    <MapPin className="size-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Office Address</h4>
                    <p className="text-primary-foreground/80 leading-relaxed">
                      2746 Leechburg Rd<br />
                      Pittsburgh, PA 15239
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-foreground/10 p-3 rounded-full shrink-0">
                    <Phone className="size-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Phone Number</h4>
                    <p className="text-primary-foreground/80 leading-relaxed">
                      (412) 758-8781
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-foreground/10 p-3 rounded-full shrink-0">
                    <Clock className="size-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Business Hours</h4>
                    <p className="text-primary-foreground/80 leading-relaxed">
                      Mon - Fri: 8:00 AM - 6:00 PM<br />
                      Sat: 9:00 AM - 2:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 relative z-10">
              <p className="text-primary-foreground/90 mb-4 font-medium">Need immediate assistance?</p>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 border-none shadow-md text-base h-12 px-8" asChild>
                <Link to="tel:4127588781">
                  <Phone className="mr-2 size-5" />
                  Call Now
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:w-7/12 p-8 md:p-12 bg-card">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">Send Us a Message</h3>
              <p className="text-muted-foreground">
                Fill out the details below and we'll get back to you within 24 hours.
              </p>
            </div>

            <FormProvider formId="free_estimate_form_home" sectionName="request-a-free-estimate" defaultValues={{
            name: "",
            email: "",
            phone: "",
            address: "",
            service_type: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Get My Free Estimate" successMessage="Thank you! We have received your estimate request and will contact you shortly to discuss your landscaping needs.">
              <EstimateForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}